import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import _pick from 'lodash/pick';

import Button from '@components/_shared/button';
import { AmenitiesSelection } from '@components/search/amenitiesSelection';
import { MinMaxInputs } from '@components/search/minMaxInputs';
import Select from '@components/_shared/select';
import { bedroomsOptions, guestsOptions } from '@components/search/utils';
import { useClickOutside } from '@utils/hooks/useClickOutside';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { FilterParamsType } from '@utils/types/searchListingsContext';
import { MinMaxSelectors } from '@components/search/minMaxSelectors';
import Checkbox from '@components/_shared/checkbox';

import styles from './styles.module.scss';

type Props = {
  activeFilters: FilterParamsType;
  onApply: (value: FilterParamsType) => void;
  filtersFromSearchbarToShow: {
    price: boolean;
    bedrooms: boolean;
    petFriendly: boolean;
  };
} & BaseComponentProps;

export function MoreFiltersButton(props: Props) {
  const { containerStyle, onApply, activeFilters, filtersFromSearchbarToShow } =
    props;
  const [filterValues, setFilterValues] = useState<FilterParamsType>(
    _pick(activeFilters, [
      'price',
      'bedrooms',
      'petFriendly',
      'beds',
      'bathrooms',
      'amenities',
      'guests',
    ])
  );
  const [showFiltersContainer, setShowFiltersContainer] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (activeFilters.beds?.min || activeFilters.beds?.max) {
      count++;
    }
    if (activeFilters.bathrooms?.min || activeFilters.bathrooms?.max) {
      count++;
    }
    if (activeFilters.amenities?.length) {
      count++;
    }
    if (activeFilters.guests) {
      count++;
    }
    if (
      filtersFromSearchbarToShow.price &&
      (activeFilters.price?.min || activeFilters.price?.max)
    ) {
      count++;
    }
    if (
      filtersFromSearchbarToShow.bedrooms &&
      (activeFilters.bedrooms?.min || activeFilters.bedrooms?.max)
    ) {
      count++;
    }
    if (filtersFromSearchbarToShow.petFriendly && activeFilters.petFriendly) {
      count++;
    }
    return count;
  }, [activeFilters]);

  const openFiltersContainer = () => {
    setShowFiltersContainer((value) => !value);
  };

  const closeFiltersContainer = () => {
    setShowFiltersContainer(false);
  };

  useClickOutside(containerRef, closeFiltersContainer);

  const onApplyClick = () => {
    onApply(filterValues);
    closeFiltersContainer();
  };

  const onFilterChange = (filter: string) => (value: any) => {
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  return (
    <div
      ref={containerRef}
      className={clsx([styles.moreFilters, containerStyle])}
    >
      <div className={styles.button} onClick={openFiltersContainer}>
        More
        {activeFiltersCount > 0 && (
          <span className={styles.moreFilters__count}>
            &nbsp;({activeFiltersCount})
          </span>
        )}
      </div>

      {showFiltersContainer && (
        <div
          className={styles.filtersContainer}
          onClick={(e) => e.stopPropagation()}
        >
          {filtersFromSearchbarToShow.price && (
            <>
              <div>
                <div className={styles.filtersContainer__title}>
                  {activeFilters.market === 'new-york-city'
                    ? 'Monthly Price'
                    : 'Price'}
                </div>
                <MinMaxInputs
                  value={filterValues.price}
                  onChange={onFilterChange('price')}
                  inputStyle={styles.filtersContainer__priceInput}
                  type="price"
                  withDebounce
                  suggestedValues={[
                    5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000,
                    45000, 50000,
                  ]}
                />
              </div>
              <div className={styles.filtersContainer__divider} />
            </>
          )}

          {filtersFromSearchbarToShow.bedrooms && (
            <>
              <div>
                <div className={styles.filtersContainer__title}>Bedrooms</div>
                <MinMaxSelectors
                  value={filterValues.bedrooms}
                  onChange={onFilterChange('bedrooms')}
                  options={bedroomsOptions}
                  selectorStyle={styles.filtersContainer__bedroomSelector}
                  containerStyle={styles.filtersContainer__bedrooms}
                  emptyValue={'Any'}
                />
              </div>

              <div className={styles.filtersContainer__divider} />
            </>
          )}

          {filtersFromSearchbarToShow.petFriendly && (
            <>
              <div>
                <div className={styles.filtersContainer__title}>Pets</div>
                <Checkbox
                  label="Pet Friendly"
                  containerStyle={styles.filtersContainer__petCheckbox}
                  onChange={onFilterChange('petFriendly')}
                  value={filterValues.petFriendly}
                />
              </div>

              <div className={styles.filtersContainer__divider} />
            </>
          )}

          <div>
            <div className={styles.filtersContainer__title}>Beds</div>
            <MinMaxInputs
              value={filterValues.beds}
              onChange={onFilterChange('beds')}
              inputStyle={styles.filtersContainer__input}
              type="rooms"
              placeholder="Any"
            />
          </div>

          <div className={styles.filtersContainer__divider} />

          <div>
            <div className={styles.filtersContainer__title}>Bathrooms</div>
            <MinMaxInputs
              value={filterValues.bathrooms}
              onChange={onFilterChange('bathrooms')}
              inputStyle={styles.filtersContainer__input}
              type="rooms"
              placeholder="Any"
            />
          </div>

          <div className={styles.filtersContainer__divider} />

          <div>
            <div className={styles.filtersContainer__title}>Party Size</div>
            <Select
              value={filterValues.guests}
              options={guestsOptions}
              onSelect={onFilterChange('guests')}
              placeholder="Guests"
              containerStyle={styles.filtersContainer__selector}
              customEmptyValue={{ label: 'Any', value: '' }}
              withRoundCorners
            />
          </div>

          <div className={styles.filtersContainer__divider} />

          <AmenitiesSelection
            value={filterValues.amenities}
            onChange={onFilterChange('amenities')}
          />

          <Button
            onClick={onApplyClick}
            containerStyle={styles.confirm}
            variant="darkTanContained"
          >
            Confirm
          </Button>
        </div>
      )}
    </div>
  );
}
